import type { MORE_MAPPING_FORM_DATA, MoreFieldMappingItem } from 'src/interfaces/Template.interface'

const toPositiveId = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

/** Recipe other_templates shapes differ (nested template vs flat fieldId/template_id). */
export const resolveOtherTemplateRef = (
  link: any
): { fieldId: number | null; templateId: number | null; linkId: number | null } => {
  const rawField =
    link?.template?.platform_field ??
    link?.platform_field ??
    link?.fieldId ??
    link?.field_id ??
    link?.platform_field_id
  const fieldId =
    rawField && typeof rawField === 'object'
      ? toPositiveId(rawField.id)
      : toPositiveId(rawField)

  const templateId = toPositiveId(
    link?.template?.id ?? link?.template_id ?? link?.templateId
  )

  // Top-level `id` is the platformtemplatemappingothertemplate row, not the template pk.
  const linkId = link?.template != null || link?.template_id != null || link?.templateId != null
    ? toPositiveId(link?.id)
    : null

  return {
    fieldId,
    templateId,
    linkId,
  }
}

export type MoreMappingIdRef = {
  fieldId: number | null
  templateId: number
  /** Junction row id on template-mapping update (avoids duplicate other_templates). */
  linkId?: number | null
}

type ResolvedOtherTemplateRef = ReturnType<typeof resolveOtherTemplateRef>

const toMoreMappingIdRef = (
  ref: ResolvedOtherTemplateRef & { templateId: number }
): MoreMappingIdRef => ({
  fieldId: ref.fieldId,
  templateId: ref.templateId,
  linkId: ref.linkId ?? null,
})

const mergeMoreMappingRef = (
  previous: MoreMappingIdRef,
  next: MoreMappingIdRef
): MoreMappingIdRef => ({
  fieldId: next.fieldId ?? previous.fieldId,
  templateId: next.templateId,
  linkId: next.linkId ?? previous.linkId ?? null,
})

export const keepMoreMappingRefs = (links: any[]): MoreMappingIdRef[] => {
  const resolved: MoreMappingIdRef[] = links
    .map(resolveOtherTemplateRef)
    .filter(
      (ref): ref is ResolvedOtherTemplateRef & { templateId: number } =>
        ref.templateId != null
    )
    .map(toMoreMappingIdRef)

  const byTemplateId = new Map<number, MoreMappingIdRef>()
  for (const ref of resolved) {
    const previous = byTemplateId.get(ref.templateId)
    if (!previous) {
      byTemplateId.set(ref.templateId, ref)
      continue
    }
    if (previous.fieldId == null && ref.fieldId != null) {
      byTemplateId.set(ref.templateId, mergeMoreMappingRef(previous, ref))
    } else if (!previous.linkId && ref.linkId) {
      byTemplateId.set(ref.templateId, mergeMoreMappingRef(previous, ref))
    }
  }

  const ordered: MoreMappingIdRef[] = []
  for (const ref of byTemplateId.values()) {
    if (ref.fieldId == null) {
      ordered.push(ref)
      continue
    }
    const existingIndex = ordered.findIndex((item) => item.fieldId === ref.fieldId)
    if (existingIndex >= 0) {
      ordered.splice(existingIndex, 1, mergeMoreMappingRef(ordered[existingIndex], ref))
    } else {
      ordered.push(ref)
    }
  }
  return ordered
}

export const fieldIdFromMoreMappingTemplate = (template: any): number | null => {
  const raw = template?.platform_field
  if (raw && typeof raw === 'object') return toPositiveId(raw.id)
  return toPositiveId(raw ?? template?.platform_field_id)
}

export const resolveExistingMoreMappingTemplateId = (
  entry: { values: MORE_MAPPING_FORM_DATA; fieldMeta: MoreFieldMappingItem },
  moreMappingIds: MoreMappingIdRef[]
): number | null => {
  const hydratedId = toPositiveId(entry.fieldMeta?.moreMappingData?.id)
  if (hydratedId) return hydratedId

  const candidates = [
    entry.fieldMeta?.id,
    entry.values?.platform_field,
    entry.fieldMeta?.moreMappingData?.platform_field?.id,
  ]
    .map(toPositiveId)
    .filter((id): id is number => id != null)

  const match = moreMappingIds.find((it) => {
    const templateId = toPositiveId(it.templateId)
    const fieldId = toPositiveId(it.fieldId)
    return templateId != null && fieldId != null && candidates.includes(fieldId)
  })

  return toPositiveId(match?.templateId)
}

export const upsertMoreMappingRef = (
  list: MoreMappingIdRef[],
  ref: MoreMappingIdRef
): MoreMappingIdRef[] => {
  const previous = list.find(
    (item) =>
      (ref.fieldId != null && item.fieldId === ref.fieldId) ||
      (ref.templateId && item.templateId === ref.templateId)
  )
  const linkId =
    ref.linkId ??
    (previous && previous.templateId === ref.templateId ? previous.linkId : null)

  const next = list.filter((item) => {
    if (ref.templateId && item.templateId === ref.templateId) return false
    if (ref.fieldId != null && item.fieldId === ref.fieldId) return false
    return true
  })
  return [...next, { ...ref, linkId: linkId ?? null }]
}

export const toRecipeOtherTemplates = (refs: MoreMappingIdRef[]) =>
  refs
    .filter((ref) => toPositiveId(ref.templateId) != null)
    .map((ref) => {
      const linkId = toPositiveId(ref.linkId)
      return {
        ...(linkId != null ? { id: linkId } : {}),
        template_id: ref.templateId,
        fieldId: ref.fieldId,
      }
    })
